import { useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { readCookie } from "../../utils/helpers";
import { initiateVerification, signInUser, verifyMfa, verifyOtp } from "../../pages/verify-user/actions";
import OtpResendTimer from "../otp-resend-timer/component";
import { getTenantGroupId } from "../../utils/general-config";
import { currentDomain } from "../../utils/general-config";
import { DomainEnum } from "../../services/enum/environment-enum";

const VerifyByEmail = ({ toggleModal, isLoading, setIsLoading, userGuid, otp, toSetOtp, onEmailVerified, emailId }) => {
  const isSignSimpli = currentDomain() === DomainEnum.SIGNSIMPLI;
  const logoSrc =
    isSignSimpli
      ? "/static/images/logos/signsimpli-new-logo.svg"
      : "/static/images/logos/pdfsimpli-new-logo.svg";
  const [showOtp, setShowOtp] = useState(true); // toggle OTP inputs - kept from original
  const [error, setError] = useState(""); // error message

  const handleChange = (e, index) => {
    const value = e.target.value.replace(/[^0-9]/g, ""); // allow only digits
    const newOtp = [...otp];
    newOtp[index] = value;
    toSetOtp(newOtp); // move to next input automatically

    if (index < 5 && value) {
      document.getElementById(`otp-input-${index + 1}`).focus();
    }
    if (index === 5 && value) {
      // We check if all digits in the *newly updated* OTP array are present
      if (newOtp.every(digit => digit !== "") && newOtp.length === 6) {
        // Trigger the verification immediately
        onVerify(newOtp);
      }
    }
  };
  const handlePaste = e => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text");

    const digits = paste.replace(/[^0-9]/g, "").slice(0, 6);
    const newOtp = [...otp]; // Start with the current state

    for (let i = 0; i < digits.length; i++) {
      newOtp[i] = digits[i];
    }
    toSetOtp(newOtp);

    const nextFocusIndex = digits.length < 6 ? digits.length : 5;

    if (document.getElementById(`otp-input-${nextFocusIndex}`)) {
      document.getElementById(`otp-input-${nextFocusIndex}`).focus();
    }

    if (digits.length === 6) {
      onVerify(newOtp);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`).focus();
    }
  };

  // Helper function for API simulation
  const onVerify = async newOtp => {
    let code = otp.join("");
    let modifiedOtp = otp;
    if (typeof newOtp === "object" && newOtp !== null) {
      code = newOtp.join("");
      modifiedOtp = newOtp;
    }
    if (code.length < 6) {
      setError("Please enter the 6-digit code.");
      return;
    }

    setError("");
    setIsLoading(true);
    const trimmedOtp = modifiedOtp.join("").trim();
    try {
      const success = await verifyOtp(userGuid || readCookie("userguid"), trimmedOtp);
      if (!success) {
        setError("The verification code you entered is invalid");
        toSetOtp(Array(6).fill("")); // Clear OTP on failure
      } else {
        onEmailVerified();
        setError("");
        toSetOtp(Array(6).fill(""));
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      // show toast / error message
      console.error("OTP verification error:", error);
    }
  };
  const handleResend = async () => {
    try {
      const payload = {
        UserGuid: userGuid || readCookie("userguid"),
        ContactType: 1,
        TenantGroupId: getTenantGroupId(),
      };
      const success = await initiateVerification(payload);
      if (!success) {
        setError("Failed to send OTP");
        toSetOtp(Array(6).fill("")); // Clear OTP on failure
      } else {
        setError("");
        toSetOtp(Array(6).fill(""));
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      // show toast / error message
      console.error("Failed to send error:", error);
    }
  };
  return (
    <>
      <div className="p-[2px] bg-white rounded-[7px] !border-0 !shadow-none">
        <div className="border-b border-gray-200 relative">
          {/* Close Button */}
          {!isSignSimpli && (
            <div className="absolute top-0 right-0 pt-4 pr-4 z-20">
              <button
                type="button"
                className="rounded-md text-gray-400 hover:text-gray-600"
                onClick={() => toggleModal(false)}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          )}
          <div className="flex pl-6 py-2">
            <img
              className="pdf-simpli-logo flex w-36 sm:w-40 lg:w-44 h-7 sm:h-9 lg:h-10"
              src={process.env.PUBLIC_URL + logoSrc}
              width="180"
              alt="Logo"
            />
          </div>
        </div>

        <div>
          <div className={`p-6 relative text-center ${isSignSimpli ? "font-rethink" : ""}`}>
            {/* Circle Icon */}
            <div className="flex justify-center mb-4">
              <div className="flex items-center justify-center w-14 h-14 rounded-full">
                {isSignSimpli ? (
                  <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="60" height="60" rx="30" fill="#1D7FFF" />
                    <path
                      d="M26.2793 31.4171L32.6579 37.7957L48.2502 22.2035"
                      stroke="#EAEBED"
                      strokeWidth="4.96"
                    />
                    <path d="M11.75 28.9372L20.2549 37.7965L35.8471 22.2042" stroke="white" strokeWidth="4.96" />
                  </svg>
                ) : (
                  <img
                    className="check-icon"
                    src={process.env.PUBLIC_URL + "/static/images/double-check-circle-green.svg"}
                    width="180"
                    alt="check icon"
                  />
                )}
              </div>
            </div>

            {/* Title */}
            <h2 className={`text-center  font-semibold text-gray-900 ${isSignSimpli ? "text-[24px] text-md-lg" : "text-lg"}`}>Check your email</h2>
            <div className="mt-2 flex items-center justify-center gap-2">
              <input
                type="text"
                value={emailId}
                readOnly
                className={`${isSignSimpli ? "w-[250px]" : ""} rounded-[5px] border border-[#E5E7EB] py-[11px] px-[15px] text-[20px] text-[#16283D] text-center focus:outline-none truncate`}
              />
              {/* <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                <path
                  d="M10.6933 2.36027L13.64 5.30694M11.9433 1.11027C12.3341 0.719522 12.8641 0.5 13.4167 0.5C13.9693 0.5 14.4992 0.719522 14.89 1.11027C15.2808 1.50103 15.5003 2.031 15.5003 2.58361C15.5003 3.13622 15.2808 3.66619 14.89 4.05694L3.41667 15.5303H0.5V12.5536L11.9433 1.11027Z"
                  stroke="black"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg> */}
            </div>

            {/* Subtitle */}
            <p className={`mt-2 text-center text-sm text-[#687585] ${isSignSimpli ? "md:px-[58px]" : ""}`}>
              Step 1 of 3: We’ve sent a 6-digit verification code to <span className="text-blue-600">{emailId}</span> —
              the code expires in 10 minutes
            </p>

            {/* OTP Inputs */}
            {showOtp && (
              <div className="flex justify-center gap-[6px] md:gap-2 mt-6">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    id={`otp-input-${i}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleChange(e, i)}
                    onKeyDown={e => handleKeyDown(e, i)}
                    onPaste={handlePaste}
                    className={`w-12 h-12 border rounded-md text-center text-xl font-semibold focus:ring-2 focus:ring-blue-500 ${
                      error ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Error message (conditional) */}
            {error && <p className="mt-2 text-center text-sm text-red-500 font-medium">{error}</p>}

            {/* Verify Button */}
            <div className="mt-6 flex justify-center">
              <button
                id="verify-email"
                type="button"
                onClick={() => onVerify()}
                disabled={isLoading}
                className={`px-20 py-4 text-white leading-[120%] !font-rethink tracking-[2%] text-md md:text-xl ${isSignSimpli ? "md:text-[18px] mb-4" : "md:text-xl"}  font-bold rounded-[100px] bg-ps-reskin-radial hover:bg-hover-ps-reskin-radial uppercase transition-colors duration-1000 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Verify Email
              </button>
            </div>

            {/* Resend & Info */}
            <div>
              <OtpResendTimer duration={100} onResend={handleResend} btnText="Resend code" />
              {/* <div className="mt-3 text-center">
            <button
              className="text-blue-600 text-sm font-medium hover:underline"
              onClick={() => {
                setError(""); // clear error on resend
                console.log("Resend code");
              }}
            >
              Resend code
            </button>
          </div> */}
            </div>
          </div>
          <div className="py-4 text-center text-xs text-gray-500 md:border-t md:border-gray-200">
            <span className="inline-flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12" viewBox="0 0 10 12" fill="none">
                <path
                  d="M8.37483 5.33217H7.85893V3.7845C7.85893 3.19695 7.71205 2.6524 7.41827 2.15083C7.1245 1.64927 6.72683 1.25161 6.22527 0.957832C5.72371 0.66406 5.17916 0.517174 4.59161 0.517174C4.00407 0.517174 3.45951 0.66406 2.95795 0.957832C2.45639 1.25161 2.05872 1.64927 1.76495 2.15083C1.47118 2.6524 1.32429 3.19695 1.32429 3.7845V5.33217H0.808397C0.52179 5.33217 0.278174 5.43249 0.0775488 5.63311C-0.123076 5.83374 -0.223389 6.07735 -0.223389 6.36396V10.4911C-0.223389 10.7777 -0.123076 11.0213 0.0775488 11.222C0.278174 11.4226 0.52179 11.5229 0.808397 11.5229H8.37483C8.66143 11.5229 8.90505 11.4226 9.10567 11.222C9.3063 11.0213 9.40661 10.7777 9.40661 10.4911V6.36396C9.40661 6.07735 9.3063 5.83374 9.10567 5.63311C8.90505 5.43249 8.66143 5.33217 8.37483 5.33217ZM6.13929 5.33217H3.04393V3.7845C3.04393 3.35458 3.1944 2.98916 3.49534 2.68822C3.79628 2.38729 4.1617 2.23682 4.59161 2.23682C5.02152 2.23682 5.38695 2.38729 5.68788 2.68822C5.98882 2.98916 6.13929 3.35458 6.13929 3.7845V5.33217Z"
                  fill="#64C897"
                />
              </svg>
              <span className="ml-2 text-[#16283D] text-[10px]"> Your information is encrypted and secure</span>
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerifyByEmail;
